import { useRef, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { getSubscribedModules } from "@neumetric/blueprint-lib/store";
import { NMPopup, Col, NMForm } from "@neumetric/blueprint-lib/components";

const BASE_FIELDS = [
  {
    attribute: "modules",
    typeRepresentation: "TREE",
    label: "Module",
    multiSelection: true,
  },
  {
    attribute: "format",
    typeRepresentation: "DROPDOWN",
    label: "Format",
    options: [
      { label: "PNG", id: "png" },
      { label: "PDF", id: "pdf" },
      { label: "CSV", id: "csv" }, // not implemented in Helpers; leave for future
    ],
  },
];

// ✅ all module keys consolidated here
const ALL_MODULES_ARRAY = [
  "export_all",
  "fusion",
  "Documenter_Documentation_Status",
  "Remediator_Department_vs_Compliance_Status",
  "Remediator_Priority_vs_Compliance_Status",
  "Remediator_Department_vs_Priority_Status",
  "Auditor_Audit_Status",
  "Auditor_overview",
  "Educator_Assessment_vs_Status",
  "Educator_Department_vs_Status",
  "Educator_Overall_Coverage",
  "Investigator_Severity_vs_Incident_Status",
  "Investigator_Department_vs_Incident_Status",
  "Continuer_Continuity_Test_Status",
  "Mitigator_Decision_Status_vs_Risk_Status",
  "Mitigator_Department_vs_Severity_Status",
  "Mitigator_Status_vs_Severity_Status",
  "Mitigator_Department_vs_Status_Status",
];

export default function ExportStatsDialog({
  open = true,
  onClose,
  onExport,
  selectedAudit,
  defaultFormat = "pdf",
  defaultModules = ["fusion"],
  auditList,
}) {
  const [formData, setFormData] = useState({
    format: defaultFormat,
    modules: defaultModules,
  });
  const modules = useSelector(getSubscribedModules);
  const _formRef = useRef(null);

  const ORDER = [
    "remediator",
    "auditor",
    "documenter",
    "continuer",
    "educator",
    "investigator",
    "mitigator",
  ];
  const buildLeavesByModule = (auditList = []) => {
    // 🔹 Step 1: Deduplicate by title
    const uniqueAudits = Array.from(
      new Map(
        (auditList || []).map((a) => [
          a.title?.trim().replace(/\s+/g, " ").toLowerCase(),
          a,
        ])
      ).values()
    );

    // 🔹 Step 2: Add id + key for auditor leaves
    const auditorLeaves = [
      {
        id: "Auditor_Audit_Status",
        key: "Auditor_Audit_Status",
        label: "Audit Stats",
      },
      ...uniqueAudits.map((a, idx) => ({
        id: `${a.title}_overview`,
        key: `${a.title}_${a.id || idx}_key`,
        label: `${a.title} Overview`,
        baseId: "Auditor_overview",
      })),
    ];

    return {
      remediator: [
        {
          id: "Remediator_Department_vs_Compliance_Status",
          key: "Remediator_Department_vs_Compliance_Status",
          label: "Compliance Stats (Department-wise)",
        },
        {
          id: "Remediator_Priority_vs_Compliance_Status",
          key: "Remediator_Priority_vs_Compliance_Status",
          label: "Compliance Stats (Priority-wise)",
        },
      ],
      auditor: auditorLeaves,
      documenter: [
        {
          id: "Documenter_Documentation_Status",
          key: "Documenter_Documentation_Status",
          label: "Documentation Stats (Department-wise)",
        },
      ],
      continuer: [
        {
          id: "Continuer_Continuity_Test_Status",
          key: "Continuer_Continuity_Test_Status",
          label: "Continuity Stats (Department-wise)",
        },
      ],
      educator: [
        {
          id: "Educator_Assessment_vs_Status",
          key: "Educator_Assessment_vs_Status",
          label: "Educator Stats (Learning-wise)",
        },
        {
          id: "Educator_Department_vs_Status",
          key: "Educator_Department_vs_Status",
          label: "Educator Stats (Department-wise)",
        },
      ],
      investigator: [
        {
          id: "Investigator_Severity_vs_Incident_Status",
          key: "Investigator_Severity_vs_Incident_Status",
          label: "Incident Stats (Severity-wise)",
        },
        {
          id: "Investigator_Department_vs_Incident_Status",
          key: "Investigator_Department_vs_Incident_Status",
          label: "Incident Stats (Department-wise)",
        },
      ],
      mitigator: [
        {
          id: "Mitigator_Decision_Status_vs_Risk_Status",
          key: "Mitigator_Decision_Status_vs_Risk_Status",
          label: "Mitigator Stats (Decision&Status vs Risk)",
        },
        {
          id: "Mitigator_Department_vs_Severity_Status",
          key: "Mitigator_Department_vs_Severity_Status",
          label: "Mitigator Stats (Department-wise-severity)",
        },
        {
          id: "Mitigator_Status_vs_Severity_Status",
          key: "Mitigator_Status_vs_Severity_Status",
          label: "Mitigator Stats (Status-wise-severity)",
        },
        {
          id: "Mitigator_Department_vs_Status_Status",
          key: "Mitigator_Department_vs_Status_Status",
          label: "Mitigator Stats (Department-wise-status)",
        },
      ],
    };
  };

  const LEAVES_BY_MODULE = useMemo(
    () => buildLeavesByModule(auditList),
    [auditList]
  );
  const idToLabelMap = useMemo(() => {
    return Object.values(LEAVES_BY_MODULE)
      .flat()
      .reduce((acc, { id, label }) => {
        acc[id] = label; // e.g., "Remediator_Department_vs_Compliance_Status" -> "Compliance Stats (Department-wise)"
        return acc;
      }, {});
  }, [LEAVES_BY_MODULE]);

  const parentLabelMap = {
    fusion: "Fusion",
    remediator: "Remediator",
    auditor: "Auditor",
    documenter: "Documenter",
    continuer: "Continuer",
    educator: "Educator",
    investigator: "Investigator",
    mitigator: "Mitigator",
  };
  const getModuleOptions = () => {
    const exportable = (modules || [])
      .filter((m) => m.id !== "console")
      .sort((a, b) => ORDER.indexOf(a.id) - ORDER.indexOf(b.id));

    const moduleNodes = exportable.map((m) => ({
      label: `${m.label} Overview`,
      id: m.id,
      children: [...(LEAVES_BY_MODULE[m.id] || [])],
    }));

    return [
      { label: "Export Everything", id: "export_all" },
      { label: "Fusion Overview", id: "fusion" },
      ...moduleNodes,
    ];
  };

  const getFields = () =>
    BASE_FIELDS.map((field) => ({
      ...field,
      labelAlign: "middle",
      classes: { root: field.attribute === "modules" ? "w-100" : "w-50" },
      options:
        field.attribute === "modules" ? getModuleOptions() : field.options,
    }));

  // handleFormChange: syncs visual NMForm + logic
  const handleFormChange = (attr, value) => {
    if (attr !== "modules") {
      setFormData((prev) => ({ ...prev, [attr]: value }));
      return;
    }

    // Fusion exclusive
    if (value.includes("fusion") && !value.includes("export_all")) {
      const next = { ...formData, modules: ["fusion"] };
      setFormData(next);
      _formRef.current?.updateFormData?.(next);
      return;
    }

    // Export Everything → select all
    if (value.includes("export_all")) {
      const next = { ...formData, modules: ALL_MODULES_ARRAY };
      setFormData(next);
      _formRef.current?.updateFormData?.(next);
      return;
    }

    // Normal
    const next = { ...formData, modules: value };
    setFormData(next);
    _formRef.current?.updateFormData?.(next);
  };

  const handleExport = () => {
    const values = _formRef.current?.getValues?.() || {};
    const format = values.format || defaultFormat;
    let modules = Array.isArray(values.modules)
      ? values.modules
      : defaultModules;

    // if (modules.includes("export_all")) {
    //   modules = ALL_MODULES_ARRAY.filter((m) => m !== "export_all");
    // }

    if (!format || !modules.length) return;
    onExport?.({ format, modules, idToLabelMap, parentLabelMap });
    setTimeout(onClose, 0);
  };

  return (
    <NMPopup
      visible={open}
      title="Export Overview Stats & Charts"
      variant="INFO"
      width="500px"
      onModalClose={onClose}
      actions={[
        {
          btnType: "OUTLINED",
          icon: "CANCEL",
          label: "Cancel",
          onClick: onClose,
        },
        {
          btnType: "CONTAINED",
          icon: "UPLOAD",
          label: "Export",
          onClick: handleExport,
        },
      ]}
    >
      <Col className="w-100 ff-roboto fs-15 line-22">
        <p className="mt-0 ff-roboto fs-15 fw-400 line-22 text-filled">
          Select the relevant Overview Stats & Charts for export. <br />
          Selecting 'Fusion' will export all Overviews for all Modules.
        </p>
        <NMForm
          ref={_formRef}
          fields={getFields()}
          formData={formData} // ✅ bound to state
          onChange={handleFormChange}
        />
        <div className="ff-roboto fs-13 fs-italic fw-400 line-16 text-filled">
          By default, entire Fusion Overview will be exported in PDF format.
        </div>
      </Col>
    </NMPopup>
  );
}
